import React from 'react';

import { isDefined } from '@nfctron/nexus-core';
import { UiButton } from '@nfctron/nexus-react';
import { EditSquareIcon } from '@nfctron/nexus-react-svg';

import { ActionIcon, Alert, Collapse, JsonInput, Text, Title } from '@mantine/core';
import { type ContextModalProps } from '@mantine/modals';
import { ChevronDownIcon, ChevronRightIcon, ShieldCheckIcon } from 'lucide-react';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { ComponentDisplayName, type ModalComponent } from '@root/lib/types/components';

/**
 * Debugger modal data section component
 * @constructor
 */
const DebuggerData: React.FunctionComponent<{
	value: unknown;
	field?: string;
	onChange?: (update: string) => void;
}> = ({ value, field, onChange }) => {
	// eslint-disable-next-line @typescript-eslint/no-use-before-define
	const { t } = useTranslate(APP_I18N_NAMESPACE.COMMON, DebuggerModal.displayName);
	const [editing, setEditing] = React.useState(false);
	const stringify = React.useMemo(() => {
		try {
			return JSON.stringify(value, null, 2);
		} catch (e) {
			return `${e}`;
		}
	}, [value]);
	const [editingValue, setEditingValue] = React.useState(stringify);
	const isError = React.useMemo(() => {
		try {
			JSON.parse(editingValue);
			return false;
		} catch (e) {
			return true;
		}
	}, [editingValue]);
	const [open, setOpen] = React.useState(false);

	return (
		<section>
			{/* header */}
			<header className="flex items-center justify-between gap-3">
				<div className="flex items-center gap-2">
					{/* toggle button */}
					<ActionIcon variant="transparent" radius="xl" onClick={() => setOpen((o) => !o)} color="lucky-point" size="md">
						{open ? <ChevronDownIcon className="text-xl" /> : <ChevronRightIcon className="text-xl" />}
					</ActionIcon>
					{/* title */}
					<Title size="h4" c="lucky-point">
						{field ?? t('debugContent.title')}
					</Title>
				</div>
				{/* update icon */}
				{editing ? (
					<UiButton
						color="green"
						leftSection={<ShieldCheckIcon />}
						disabled={isError}
						onClick={() => {
							onChange?.(editingValue);
							setEditing(false);
						}}
					>
						{t('saveButton.text')}
					</UiButton>
				) : (
					<UiButton
						color="lucky-point"
						leftSection={<EditSquareIcon />}
						disabled={!isDefined(onChange)}
						onClick={() => {
							setEditing(true);
							setOpen(true);
						}}
					>
						{t('editButton.text')}
					</UiButton>
				)}
			</header>

			{/* debug content */}
			<Collapse in={open}>
				{/* content */}
				{editing ? (
					<JsonInput
						variant="filled"
						placeholder={stringify}
						value={editingValue}
						onChange={(val) => setEditingValue(val)}
						autosize
						radius="md"
						minRows={stringify.split(/\r\n|\r|\n/g).length}
						classNames={{
							input: 'leading-relaxed! bg-gray-100!',
						}}
					/>
				) : (
					<pre className="text-md overflow-scroll rounded-md bg-gray-50 p-2 leading-relaxed text-black">{stringify}</pre>
				)}
			</Collapse>
		</section>
	);
};

/**
 * DebuggerModal modal
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const DebuggerModal: ModalComponent<ContextModalProps<DebuggerModalProps>> = ({
	id,
	context,
	innerProps: { value: valueProp, onChange, actions },
}) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.COMMON, DebuggerModal.displayName);
	/** format the incoming value */
	const value = React.useMemo(() => (Array.isArray(valueProp) ? new Map(valueProp) : valueProp), [valueProp]);

	return (
		<div className="flex grow flex-col gap-4">
			{/* alert */}
			<Alert color="orange" variant="filled" icon={<span className="text-xl">🐛️</span>} radius="md">
				<Text c="white" size="md">
					{t('alert.text')}
				</Text>
			</Alert>

			{/* actions */}
			{isDefined(actions) && actions}

			<div className="flex grow flex-col gap-4">
				{/* debugger data */}
				{value instanceof Map ? (
					Array.from(value.entries()).map(([field, data], index) => (
						<DebuggerData
							key={field}
							value={data}
							field={field}
							onChange={
								isDefined(onChange)
									? (partial) => {
											const newMap = new Map(value);
											newMap.set(field, JSON.parse(partial));
											onChange?.(newMap);
										}
									: undefined
							}
						/>
					))
				) : (
					<DebuggerData value={value} onChange={(v) => onChange?.(JSON.parse(v))} />
				)}
			</div>
		</div>
	);
};

/**
 * DebuggerModal modal props type
 * @export
 */
export type DebuggerModalProps = {
	value: object | [string, object][];
	onChange?: (value: object | Map<string, object>) => void;
	actions?: React.ReactNode;
};

/**
 * DebuggerModal modal display name
 * @type {ComponentDisplayName}
 */
DebuggerModal.displayName = 'modal/DebuggerModal';
